/** Layout/sidebar/breakpoint constants. */
export const LAYOUT = {
  SIDEBAR_WIDTH_REM: 16,
  SIDEBAR_WIDTH_PX: 256,
  SIDEBAR_COLLAPSED_WIDTH_PX: 64,
  HEADER_HEIGHT_REM: 3.5,
  HEADER_HEIGHT_PX: 56,
  HEADER_HEIGHT_CLASS: "min-h-14",
  TOUCH_TARGET_MIN_PX: 44,
} as const;

export const BREAKPOINT = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  "2XL": 1536,
} as const;

export const SPACING = {
  /** Admin density: page X matches header + sidebar leaf (`px-3` / `md:px-4`). */
  PAGE_PADDING_X: "px-3 md:px-4",
  PAGE_PADDING_Y: "pb-3 pt-3 md:pb-4 md:pt-3",
  PAGE_PADDING: "px-3 pb-3 pt-3 md:px-4 md:pb-4 md:pt-3",
  /** `md:pb-4` — list table fill sits this far above the viewport. */
  PAGE_PADDING_BOTTOM_PX: 16,
  CARD_PADDING: "p-2.5",
  CARD_PADDING_COMPACT: "p-2.5",
  CARD_PADDING_LARGE: "p-3",
  CARD_HEADER_PADDING: "p-2.5",
  CARD_CONTENT_PADDING: "p-2.5",
  CARD_CONTENT_PADDING_NONE: "p-0",
  /**
   * List/report table cells — compact ops density (Intake DraftsList SoT).
   * Prefer {@link LIST_TABLE_*} in platform list tables; do not use raw on forms/chat.
   */
  TABLE_CELL: "px-2 py-1",
  TABLE_CELL_PX: "px-2",
  TABLE_CELL_PY: "py-1",
  /** Control/input/chat inset — same density as table cells, non-table surfaces only. */
  CONTROL_PAD: "px-2 py-1",
  CONTROL_PAD_X: "px-2",
  CONTROL_PAD_Y: "py-1",
  SECTION_GAP: "gap-2.5",
  SECTION_GAP_LARGE: "gap-3",
  PAGE_SECTION_GAP: "space-y-2.5",
  PAGE_SECTION_GAP_LARGE: "space-y-3",
  EMPTY_STATE_PADDING: "py-3",
  HEADER_PADDING: "px-3 md:px-4",
  INPUT_LEFT_ICON: "pl-8",
  INPUT_HEIGHT: "h-9",
  INPUT_HEIGHT_MEDIUM: "min-h-10",
  INPUT_ICON_INSET_LEFT: "left-2",
  BUTTON_ICON_SIZE: "h-10 w-10",
  SCROLL_PADDING_RIGHT: "pr-3",
  POSITION_BOTTOM_TIGHT: "bottom-2",
  LIST_PADDING_LEFT_NONE: "pl-0",
  GAP_TIGHT: "gap-1",
  GAP_SM: "gap-1.5",
  GAP_DEFAULT: "gap-2",
  GAP_MEDIUM: "gap-2",
  GAP_LARGE: "gap-2.5",
  MARGIN_BOTTOM: "mb-2",
  MARGIN_BOTTOM_LARGE: "mb-3",
  MARGIN_TOP: "mt-2",
  MARGIN_TOP_LARGE: "mt-3",
  MARGIN_TOP_TIGHT: "mt-1",
  MARGIN_TOP_TINY: "mt-0.5",
  MARGIN_TOP_NEG: "-mt-1",
  MARGIN_LEFT: "ml-3",
  MARGIN_LEFT_TIGHT: "ml-1.5",
  MARGIN_LEFT_TINY: "ml-0.5",
  MARGIN_RIGHT: "mr-3",
  MARGIN_RIGHT_TIGHT: "mr-1.5",
  CHAT_VIEWPORT: "min-h-[360px] max-h-[640px] overflow-y-auto lg:min-h-[620px] lg:max-h-[780px]",
  SIDEBAR_NAV_GAP: "gap-1",
  SIDEBAR_CHILD_INDENT: "ml-3",
  SIDEBAR_ITEM_PADDING: "px-3 py-2",
  SIDEBAR_CHILD_GAP: "space-y-1",
  SIDEBAR_PARENT_CHILD_MARGIN: "mt-1",
  SIDEBAR_NAV_PADDING: "px-3 py-2",
  DASHBOARD_CARD_MIN_H: "min-h-[280px]",
  /** Board/list filter controls — readable min width when wrapping. */
  FILTER_CONTROL_MIN_W: "min-w-[12rem]",
  FILTER_CONTROL_MIN_W_WIDE: "min-w-[14rem]",
  /** Task board fills remaining viewport under hub chrome. */
  TASK_BOARD_VIEWPORT_H: "h-[min(100%,calc(100dvh-14rem))] max-h-[calc(100dvh-14rem)] min-h-[20rem]",
  /** Board columns grow evenly when viewport is wide; never shrink below 240px. */
  TASK_BOARD_COLUMN: "flex flex-1 basis-[240px] min-w-[240px]",
  TASK_BOARD_COLUMN_WIDTH_PX: 240,
  TASK_BOARD_COLUMN_GAP_PX: 10,
  TASK_BOARD_SCROLLBAR: "task-board-h-scroll",
  TASK_BOARD_SCROLL_TRACK: "mt-1 h-2.5 w-full shrink-0 rounded-full bg-muted",
  TASK_BOARD_SCROLL_THUMB: "h-full min-w-[2.5rem] rounded-full bg-muted-foreground/45",
  TASK_BOARD_EMPTY_PAD: "px-1 py-2",
  TASK_BOARD_SCROLL_BTN_PAD: "px-1.5 py-2",
  SECTION_RULE_PAD_T: "pt-2",
  SECTION_META_PAD_B: "pb-1",
  SECTION_META_GAP: "gap-2",
  CHIP_PAD: "px-1.5 py-0.5",
  CHIP_PAD_TIGHT: "px-1 py-0.5",
  CHIP_PAD_MD: "px-2 py-0.5",
  CONTROL_HEIGHT_SM: "h-8",
  CONTROL_HEIGHT_SM_MIN: "h-8 min-h-8",
  CONTROL_HEIGHT_XS: "h-7 min-h-7",
  AVATAR_MICRO: "h-3.5 w-3.5",
  ICON_BTN_PAD: "p-0.5",
  COLOR_SWATCH_W: "w-12",
} as const;
